import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Loader } from '@huggg/react-components';
import {
  initiateSession,
  startPolling,
  stopPolling,
  fetchStatus,
  setSelectedAccount,
  clearError,
  getIsInitiating,
  getError,
  getBankClaimStatus,
  getBankAccounts,
  getBankClaimId,
  getHasToken,
  getHasPolled,
  getBankClaimFailureReason,
} from 'redux/modules/bankPayment';
import {
  generate as generateVoucher,
  getIsGenerating,
  getRecoverableError,
  clearError as clearVoucherError,
} from 'redux/modules/voucher';
import { getHuggg } from 'redux/modules/huggg';
import { getSelectedSegment } from 'redux/modules/selectedSegment';
import { getRedemptionMethodID } from 'utilities/redemption';
import { ConnectBankView } from './views/ConnectBankView';
import { WaitingForConsentView } from './views/WaitingForConsentView';
import { AccountPickerView } from './views/AccountPickerView';
import { RetryView } from './views/RetryView';
import { NameMismatchView } from './views/NameMismatchView';
import styles from './bankPaymentRedemption.module.scss';

const LANG = 'en';
const COUNTRY = 'GB';

const buildTokenSessionUrl = (redirectUrl) => {
  const url = new URL(redirectUrl);
  url.searchParams.set('lang', LANG);
  url.searchParams.set('country', COUNTRY);
  return url.toString();
};

export const BankPaymentRedemptionUnconnected = (props) => {
  const {
    huggg,
    selectedSegment,
    status,
    accounts,
    bankClaimId,
    hasToken,
    failureReason,
    hasPolled,
    isInitiating,
    error,
    isGenerating,
    recoverableError,
    initiateSession,
    startPolling,
    stopPolling,
    fetchStatus,
    setSelectedAccount,
    generateVoucher,
    clearError,
    clearVoucherError,
  } = props;

  useEffect(() => {
    startPolling(huggg.id);
    return () => stopPolling();
  }, [huggg.id, startPolling, stopPolling]);

  const onConnectBank = useCallback(() => {
    if (error) clearError();
    return initiateSession(huggg.id)
      .then(({ redirectUrl }) => {
        window.location.assign(buildTokenSessionUrl(redirectUrl));
      })
      .catch(() => {
        // Error already in Redux state; the active view renders it.
      });
  }, [huggg.id, error, initiateSession, clearError]);

  const onSelectAccount = useCallback(
    (account) => {
      if (recoverableError) clearVoucherError();
      setSelectedAccount(account);
      return generateVoucher(
        huggg.id,
        selectedSegment && selectedSegment.id,
        getRedemptionMethodID(huggg),
        {
          bankClaimId,
          accountNumber: account.accountNumber,
          sortCode: account.sortCode,
        },
      ).then((result) => {
        // A successful claim returns the voucher and the huggg pipeline takes
        // over (success screen). A failed claim resolves to undefined and has
        // already marked the BankClaim terminal server-side, so refresh status
        // once to surface FAILED (name-mismatch / retry) — we stopped the
        // background poll at the picker, so nothing else would pick it up.
        if (!result) {
          fetchStatus(huggg.id);
        }
        return result;
      });
    },
    [
      huggg,
      selectedSegment,
      bankClaimId,
      recoverableError,
      setSelectedAccount,
      generateVoucher,
      fetchStatus,
      clearVoucherError,
    ],
  );

  // Until the first poll resolves we don't yet know whether a claim exists.
  // Show the app's standard loader rather than flashing the "connect your bank"
  // screen — most visible right after the Token redirect, where a claim is
  // already in flight.
  if (!hasPolled) {
    return (
      <div
        className={styles.container}
        data-test-id="bank-payment-loading-view"
      >
        <Loader />
      </div>
    );
  }

  switch (status) {
    case null:
    case undefined:
      return (
        <ConnectBankView
          onConnect={onConnectBank}
          isLoading={isInitiating}
          error={error}
          onDismissError={clearError}
        />
      );

    case 'INITIATED':
      return (
        <WaitingForConsentView
          onRelaunch={onConnectBank}
          isLoading={isInitiating}
          error={error}
        />
      );

    case 'CALLBACK_RECEIVED':
      return (
        <AccountPickerView
          accounts={accounts || []}
          isLoadingAccounts={!accounts || (hasToken && accounts.length === 0)}
          isClaiming={isGenerating}
          recoverableError={recoverableError}
          onSelectAccount={onSelectAccount}
        />
      );

    case 'COMPLETED':
      // Server-side claim succeeded; the voucher pipeline has already refetched
      // the huggg, so the parent will switch to VoucherClaimed on the next render.
      // Render a brief spinner to bridge that interval.
      return (
        <div
          className={styles.container}
          data-test-id="bank-payment-completed-view"
        >
          <div
            className={styles.spinner}
            data-test-id="bank-payment-completed-spinner"
          />
        </div>
      );

    case 'FAILED':
      if (failureReason === 'name-on-account-mismatch') {
        return (
          <NameMismatchView
            onTryAgain={onConnectBank}
            isLoading={isInitiating}
          />
        );
      }
      return <RetryView onRetry={onConnectBank} isLoading={isInitiating} />;

    default:
      return null;
  }
};

BankPaymentRedemptionUnconnected.propTypes = {
  huggg: PropTypes.object.isRequired,
  selectedSegment: PropTypes.object,
  status: PropTypes.string,
  accounts: PropTypes.array,
  bankClaimId: PropTypes.string,
  hasToken: PropTypes.bool,
  failureReason: PropTypes.string,
  hasPolled: PropTypes.bool,
  isInitiating: PropTypes.bool,
  error: PropTypes.any,
  isGenerating: PropTypes.bool,
  recoverableError: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
  }),
  initiateSession: PropTypes.func.isRequired,
  startPolling: PropTypes.func.isRequired,
  stopPolling: PropTypes.func.isRequired,
  fetchStatus: PropTypes.func.isRequired,
  setSelectedAccount: PropTypes.func.isRequired,
  generateVoucher: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  clearVoucherError: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  huggg: getHuggg(state),
  selectedSegment: getSelectedSegment(state),
  status: getBankClaimStatus(state),
  accounts: getBankAccounts(state),
  bankClaimId: getBankClaimId(state),
  hasToken: getHasToken(state),
  failureReason: getBankClaimFailureReason(state),
  hasPolled: getHasPolled(state),
  isInitiating: getIsInitiating(state),
  error: getError(state),
  isGenerating: getIsGenerating(state),
  recoverableError: getRecoverableError(state),
});

export const BankPaymentRedemption = connect(mapStateToProps, {
  initiateSession,
  startPolling,
  stopPolling,
  fetchStatus,
  setSelectedAccount,
  generateVoucher,
  clearError,
  clearVoucherError,
})(BankPaymentRedemptionUnconnected);

export default BankPaymentRedemption;
